import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	TreeAccordion,
	TreeView,
	TreeViewHandle,
	TreeViewItem
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	DocumentsHierarchyNode,
	EnabledView
} from "client_legacy/Documents/SharePoint/DocumentsViewsPane/types";
import {
	DocumentsViewContainerViewModel,
	getSiteContent,
	LoadedSiteContentViewModel
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import { DocumentsViewModel, DocumentsViewType } from "client/Api/Documents/SharePoint/common";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import {
	buildItemsData,
	getParentNodes,
	isSharePointContainerItem,
	NodeContent,
	NodeContentProps,
	prepareSidebarStructure
} from "../../SharePoint/SharePointSideBarMenu";
import { ViewsLoadingState } from "./SharepointPickerStore";
import { SharePointItemPickerEnabledOptions } from "../ItemPicker";
import {
	AlertManager,
	useAlerts,
	useIsMounted,
	useStableImperativeCallback
} from "client/Hooks";
import { EMPTY_ARRAY } from "client/Misc/utils";

export type SideBarMenuHandle = {
	expandParentNodes: (viewId: string) => void;
};

export type SideBarMenuProps = {
	viewsLoadingState: ViewsLoadingState;
	onSelectView(view: DocumentsViewModel): void;
	selectedView: DocumentsViewModel | undefined;
	onReloadSideBar(): void;
	options: SharePointItemPickerEnabledOptions;
	onSidebarLoadingFailure(reloadSidebar: () => void): void;
	onSiteContentLoaded: (id: string, content: LoadedSiteContentViewModel) => void;
};

const ITEM_PICKER_SHAREPOINT_ID: string = "item-picker-share-point";

export const SideBarMenu = forwardRef(
	(props: SideBarMenuProps, ref: Ref<SideBarMenuHandle>): ReactElement => {
		const {
			viewsLoadingState,
			onSelectView,
			selectedView,
			onReloadSideBar,
			options,
			onSidebarLoadingFailure,
			onSiteContentLoaded: onSiteContentLoadedFromProps
		} = props;

		const [siteContentBeingLoaded, setSiteContentBeingLoaded] = useState<ReadonlySet<string>>(new Set<string>());

		const treeRef: RefObject<TreeViewHandle<DocumentsHierarchyNode> | null> = useRef<TreeViewHandle<DocumentsHierarchyNode>>(null);

		const alertManager: AlertManager = useAlerts();
		const { isExpanded, toggleExpansion } = useToggleExpansion([ITEM_PICKER_SHAREPOINT_ID]);
		const isMounted: () => boolean = useIsMounted();

		const preparedStructure = useMemo(
			(): readonly DocumentsHierarchyNode[] => {
				if (viewsLoadingState.kind !== "success") {
					return EMPTY_ARRAY;
				}

				const filteredItems: DocumentsViewContainerViewModel[] = filterViews(viewsLoadingState.items, options);

				return prepareSidebarStructure(filteredItems);
			},
			[options, viewsLoadingState]
		);

		const itemsDataMap = useMemo(
			(): Map<string, DocumentsHierarchyNode> => {
				return buildItemsData(preparedStructure);
			},
			[preparedStructure]
		);

		const enabledItemsIds = useMemo(
			(): string[] | "all" | "none" => {
				const availableDestination: EnabledView = options.availableDestination;
				if (availableDestination.kind === "all") {
					return "all";
				}

				if (availableDestination.kind === "none") {
					return "none";
				}

				return [
					...getParentNodes(availableDestination.viewId, itemsDataMap).map(n => n.id),
					availableDestination.viewId
				];
			},
			[options.availableDestination, itemsDataMap]
		);

		const treeItems = useMemo(
			(): TreeViewItem<DocumentsHierarchyNode>[] => {
				return preparedStructure.map(
					i => convertToTreeViewItem(
						i,
						enabledItemsIds,
						options,
						siteContentBeingLoaded
					)
				);
			},
			[
				enabledItemsIds,
				options,
				preparedStructure,
				siteContentBeingLoaded
			]
		);
		const headerClickHandler = useCallback(
			(): void => onSidebarLoadingFailure(onReloadSideBar),
			[onSidebarLoadingFailure, onReloadSideBar]
		);

		const selectHandler = useCallback(
			(item: DocumentsHierarchyNode): void => {
				if (isSharePointContainerItem(item)) {
					return;
				}

				onSelectView(item);
			},
			[onSelectView]
		);

		const onSiteContentLoaded: (id: string, content: LoadedSiteContentViewModel) => void = useStableImperativeCallback(onSiteContentLoadedFromProps);

		const expandHandler = useCallback(
			async (item: DocumentsHierarchyNode): Promise<void> => {

				if (!isSharePointContainerItem(item)) {
					throw Error("Expanded item is not a container");
				}

				let { content } = item;
				if (content.isLoaded) {
					return;
				}

				setSiteContentBeingLoaded(
					(x): ReadonlySet<string> => new Set(x)
						.add(item.id)
				);

				try {
					content = await getSiteContent(item.url);
				} catch (e) {
					console.error(e);

					content = {
						isLoaded: true,
						hasError: true,
						subContainers: [],
						views: []
					};
				} finally {
					if (!isMounted()) {
						return;
					}

					setSiteContentBeingLoaded(
						(x): ReadonlySet<string> => {
							const result = new Set(x);
							result.delete(item.id);
							return result;
						}
					);
				}

				if (content.isLoaded && content.hasError) {
					alertManager.showError(localize("_FailedToLoadDocumentLibraries"));
				}

				onSiteContentLoaded(item.id, content);
			},
			[
				alertManager,
				isMounted,
				onSiteContentLoaded
			]
		);

		const expandParentNodes = useCallback(
			(id: string): void => {
				if (!treeRef.current) {
					throw Error("Tree's ref is not set");
				}

				if (itemsDataMap.size === 0) {
					throw Error("There is no data for the items");
				}

				const parentNodes: readonly DocumentsHierarchyNode[] = getParentNodes(id, itemsDataMap);

				if (parentNodes.length === 0) {
					return;
				}

				treeRef.current.expandNodes(parentNodes);
			},
			[itemsDataMap]
		);

		useImperativeHandle(
			ref,
			(): SideBarMenuHandle => ({
				expandParentNodes
			}),
			[expandParentNodes]
		);

		const toggleExpansionHandler = useCallback(
			(): void => toggleExpansion(ITEM_PICKER_SHAREPOINT_ID),
			[toggleExpansion]
		);

		return (
			<>
				<TreeAccordion trackingId="item-picker-share-point-sidebar">
					<TreeAccordion.Node>
						<TreeAccordion.Item
							{...(
								viewsLoadingState.kind === "success"
									? {
										expansionToggle: "visible",
										isExpanded: isExpanded(ITEM_PICKER_SHAREPOINT_ID),
										onToggleExpansion: toggleExpansionHandler
									}
									: {
										expansionToggle: "hidden"
									}
							)}
							icon="cloud"
							onClick={viewsLoadingState.kind === "error"
								? headerClickHandler
								: toggleExpansionHandler
							}
							trackingId="item-picker-share-point-sidebar__header"
						>
							<NodeContent
								text={localize("_SharePointStoreTitle")}
								status={viewsLoadingState.kind}
							/>
						</TreeAccordion.Item>
						{viewsLoadingState.kind === "success" && (
							<TreeView
								ref={treeRef}
								value={selectedView}
								onChange={selectHandler}
								items={treeItems}
								compareBy="id"
								onToggleExpand={expandHandler}
								indentionLevelOffset={1}
								isCollapsed={!isExpanded(ITEM_PICKER_SHAREPOINT_ID)}
								trackingId="item-picker-share-point-tree-view"
							/>
						)}
					</TreeAccordion.Node>
				</TreeAccordion>
			</>
		);
	}
);

SideBarMenu.displayName = "SideBarMenu";

function checkIsItemDisabled(
	itemId: string,
	enabledIds: "all" | "none" | string[]
): boolean {
	if (enabledIds === "all") {
		return false;
	} else if (enabledIds === "none") {
		return true;
	} else {
		return !enabledIds.includes(itemId);
	}
}

function filterViews(
	containers: readonly DocumentsViewContainerViewModel[],
	options: SharePointItemPickerEnabledOptions
): DocumentsViewContainerViewModel[] {
	const result: DocumentsViewContainerViewModel[] = [];

	for (const container of containers) {
		const { content } = container;

		if (!content.isLoaded) {
			result.push(container);
		} else {
			const filteredViews: DocumentsViewModel[] = content.views
				.filter(v => isViewVisible(v, options));

			result.push({
				...container,
				content: {
					...content,
					subContainers: filterViews(content.subContainers, options),
					views: filteredViews
				}
			});
		}
	}

	return result;
}

function convertToTreeViewItem(
	node: DocumentsHierarchyNode,
	enabledIds: "all" | "none" | string[],
	options: SharePointItemPickerEnabledOptions,
	siteContentBeingLoaded: ReadonlySet<string>
): TreeViewItem<DocumentsHierarchyNode> {
	const isDisabled: boolean = checkIsItemDisabled(node.id, enabledIds);

	let title: string | undefined = undefined;

	const availableDestination: EnabledView = options.availableDestination;

	if (availableDestination.kind !== "all" && isDisabled) {
		title = availableDestination.reason;
	}

	let status: NodeContentProps["status"];
	let children: TreeViewItem<DocumentsHierarchyNode>["children"] = undefined;

	if (isSharePointContainerItem(node)) {
		const { id, content } = node;
		if (content.isLoaded) {
			children = [
				...content.subContainers,
				...content.views
			]
				.map(n => convertToTreeViewItem(
					n,
					enabledIds,
					options,
					siteContentBeingLoaded
				));

			if (content.hasError) {
				status = "error";

				if (!isDisabled) {
					title = localize("_FailedToLoadDocumentLibraries");
				}
			}
		} else {
			children = "lazy";

			if (siteContentBeingLoaded.has(id)) {
				status = "loading";
			}
		}
	}

	return {
		value: node,
		content: () => (
			<NodeContent
				text={node.title}
				status={status}
			/>
		),
		isDisabled,
		title,
		children
	};
}

export function isViewVisible(
	view: DocumentsViewModel,
	options: SharePointItemPickerEnabledOptions
): boolean {
	const viewShouldBeHidden =
		(view.type === DocumentsViewType.RecycleBinView)
		|| (view.type === DocumentsViewType.RecentlyModified)
		|| (view.type === DocumentsViewType.SharedWithMeView)
		|| (!options.shouldShowPersonalViews && view.isPersonal);

	return !viewShouldBeHidden;
};